import React, { useCallback, useEffect, useState } from "react";
import { Card, Row, Col, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { setFilters } from "../capitalBalanceSlice";
import { SearchOutlined } from "@ant-design/icons";
import RecordDateFilter from "../../../../components/filters/RecordDateFilter";
import SearchListAutoComplete from "../../../../components/SearchListAutoComplete";
import {
  fetchCapitalBalance,
  fetchCapitalAccountsBySearch,
} from "../capitalBalanceThunk";
import { useLocation, useSearchParams } from "react-router-dom";
import { useDefaultDateRange } from "../../../../hooks/useDefaultDateRange";
import CommonDateRangeFilter from "../../../../components/common/filters/CommonDateRangeFilter";

const CapitalBalanceFilters = ({ messageApi }) => {
  const dispatch = useDispatch();
  const { filters } = useSelector((state) => state.capitalBalance);
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  useDefaultDateRange(setFilters, filters, "today");

  // handleFilterChange only queues the update, so running the report right here
  // would still use the old (default) filters — flag it and run it from the
  // next effect, once the drill-down filters have been applied.
  const [drillReady, setDrillReady] = useState(false);
  useEffect(() => {
    const path = pathname.split("/")[3];
    if (path) {
      handleFilterChange("filterType", "Date Wise");
      const fromDate = searchParams.get("fromDate");
      if (fromDate) {
        handleFilterChange("fromDate", fromDate);
      }
      handleFilterChange("toDate", path);
      setDrillReady(true);
    }
  }, []);
  useEffect(() => {
    if (drillReady) handleSubmit();
  }, [drillReady]);

  const handleFilterChange = (field, value) => {
    if (field === "filterType") {
      dispatch(setFilters({ accId: null, fromDate: null, toDate: null }));
    }
    dispatch(setFilters({ [field]: value }));
  };

  const searchAccount = useCallback(
    (text) => dispatch(fetchCapitalAccountsBySearch(text)),
    [dispatch]
  );

  const handleSubmit = () => {
    if (!filters.filterType) {
      messageApi.warning("Select Filter Type");
      return;
    }
    dispatch(fetchCapitalBalance({ filters })).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        messageApi.success("Report loaded ✅");
      } else {
        messageApi.error("Failed to fetch report ❌");
      }
    });
  };

  return (
    <Card className="mb-4 shadow-md rounded-xl">
      <Row gutter={[16, 16]} style={{ alignItems: "center" }}>
        {/* Filter Type */}
        <Col xs={24} sm={12} md={12} lg={8} xl={6}>
          <label>Filter Type</label>
          <SearchListAutoComplete
            optionsList={[
              {
                sale_id: "All Capital Account",
                display_text: "All Capital Account",
              },
              {
                sale_id: "Capital Account Wise",
                display_text: "Capital Account Wise",
              },
              { sale_id: "Date Wise", display_text: "Date Wise" },
            ]}
            value={
              filters.filterType
                ? { value: filters.filterType, label: filters.filterType }
                : undefined
            }
            onSelect={(obj) => {
              if(!obj) {
                handleFilterChange("filterType", null);
                return;
              } 
              handleFilterChange("filterType", obj?.sale_id);
            }}
            placeholder="Filter Type"
            showAddNew={false}
          />
        </Col>

        {/* Account Dropdown */}
        {filters.filterType === "Capital Account Wise" && (
          <Col xs={24} sm={12} md={12} lg={8} xl={6}>
            <label>Search Account</label>
            <SearchListAutoComplete
              entityType="account"
              searchThunk={searchAccount}
              resultSelector={(state) => state.capitalBalance.capitalAccounts}
              onSelect={(obj) => {
              if(!obj) {
                handleFilterChange("accId", null);
                return;
              } 
              handleFilterChange("accId", obj?.sale_id);
            }}
              placeholder="Account"
              showAddNew={false}
            />
          </Col>
        )}

        {/* Date Range */}
        {filters.filterType === "Date Wise" && (
          <CommonDateRangeFilter
            filters={filters}
            onChange={(fromDate, toDate) => {
            handleFilterChange("fromDate", fromDate);
            handleFilterChange("toDate", toDate);
            }}
          />
        )}

        {/* Submit */}
        <Col
          xs={24}
          sm={12}
          md={6}
          lg={4}
          style={{ display: "flex", alignItems: "flex-end", height: 58 }}
        >
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSubmit}
            style={{height: 35.19}}
            block
          >
            Report
          </Button>
        </Col>
      </Row>
    </Card>
  );
};

export default CapitalBalanceFilters;
